import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserCheck,
  UserX,
  UserPlus,
  Building2,
  Calendar,
  MessageSquare,
  Link2,
  BarChart3,
  Network,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Heart,
  Mail,
  Linkedin,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { useDevice } from '@/hooks/useDeviceType';
import { api } from '@/lib/api';
import { displayName } from '@/lib/utils';
import { PARTICIPANT_TYPE_LABELS, SESSION_TYPE_LABELS } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveEvent } from '@/hooks/useActiveEvent';
import { toast } from 'sonner';
import {
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

// ── Constants ──

const COLORS: Record<string, string> = {
  student: '#10b981',
  nps_staff: '#3b82f6',
  alumni: '#8b5cf6',
  industry: '#f59e0b',
  active_reserve_military: '#ef4444',
  government_employee: '#16a34a',
  other: '#d97706',
  admin: '#ef4444',
  staff: '#8b5cf6',
};

const TRACK_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4',
];

const ORG_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

// ── Types ──

type EventAnalyticsData = {
  realTimeMetrics: {
    totalRegistered: number;
    checkedIn: number;
    checkInRate: number;
    noShows: number;
    walkIns: number;
    checkInsLastHour: number;
    checkInsByMethod: Array<{ method: string; count: number }>;
  };
  demographics: {
    byParticipantType: Array<{ participantType: string; count: number }>;
    byRole: Array<{ role: string; count: number }>;
    byOrganization: Array<{ organization: string; count: number }>;
  };
  sessionAnalytics: {
    sessions: Array<{
      id: string;
      title: string;
      sessionType: string;
      capacity: number;
      confirmed: number;
      fillRate: number;
      startTime: string;
    }>;
    rsvpsByStatus: Array<{ status: string; count: number }>;
    typePopularity: Array<{ sessionType: string; count: number }>;
    totalCapacity: number;
    totalConfirmed: number;
  };
  networkingEngagement: {
    totalConnections: number;
    connectionsLast24h: number;
    connectionsByMethod: Array<{ method: string; count: number }>;
    totalMessages: number;
    totalConversations: number;
    connectionGraph: Record<string, Record<string, number>>;
    projectInterest: Array<{
      id: string;
      title: string;
      interested: number;
      stage: string;
    }>;
    partnerInterest: Array<{
      id: string;
      name: string;
      interested: number;
      organizationType: string | null;
    }>;
  };
};

type DrillDownUser = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  organization?: string;
  department?: string;
  role?: string;
  participantType?: string;
  linkedinUrl?: string;
  avatarUrl?: string | null;
};

type DrillDownSession = {
  id: string;
  title: string;
  sessionType: string;
  startTime: string;
  capacity: number | null;
  confirmedCount: number;
};

// ── Component ──

export default function Analytics() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDesktop } = useDevice();
  const { event: activeEvent } = useActiveEvent();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EventAnalyticsData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Drill-down dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMode, setDialogMode] = useState<'users' | 'sessions'>('users');
  const [dialogUsers, setDialogUsers] = useState<DrillDownUser[]>([]);
  const [dialogSessions, setDialogSessions] = useState<DrillDownSession[]>([]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const url = activeEvent?.id
        ? `/admin/event-analytics?eventId=${activeEvent.id}`
        : '/admin/event-analytics';
      const response = await api.get<{ success: boolean; data: EventAnalyticsData }>(url);
      if (response.success) {
        setData(response.data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [activeEvent?.id]);

  useEffect(() => {
    if (!user?.roles?.includes('admin')) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [user, navigate, fetchAnalytics]);

  // ── Drill-down handlers ──

  const openDrillDown = (title: string, mode: 'users' | 'sessions' = 'users') => {
    setDialogTitle(title);
    setDialogMode(mode);
    setDialogOpen(true);
    setDialogLoading(true);
    setDialogUsers([]);
    setDialogSessions([]);
  };

  const handleOrgDrillDown = async (orgName: string) => {
    openDrillDown(orgName);
    try {
      const response = await api.get<{ success: boolean; data: { members: DrillDownUser[]; count: number } }>(
        `/admin/organizations/${encodeURIComponent(orgName)}/members`
      );
      if (response.success && response.data) {
        setDialogUsers(response.data.members);
      }
    } catch {
      toast.error('Failed to load organization members');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSessionDrillDown = async (sessionId: string, sessionTitle: string) => {
    openDrillDown(sessionTitle);
    try {
      const response = await api.get<{ success: boolean; data: { rsvps: Array<{ user: DrillDownUser; status: string }> } }>(
        `/admin/sessions/${sessionId}/rsvps`
      );
      if (response.success && response.data) {
        setDialogUsers(response.data.rsvps.map(r => r.user));
      }
    } catch {
      toast.error('Failed to load session registrants');
    } finally {
      setDialogLoading(false);
    }
  };

  const handlePartnerDrillDown = async (partnerId: string, partnerName: string) => {
    openDrillDown(partnerName);
    try {
      const response = await api.get<{ success: boolean; data: { interests: Array<{ user: DrillDownUser }> } }>(
        `/admin/partners/${partnerId}/interests`
      );
      if (response.success && response.data) {
        setDialogUsers(response.data.interests.map(i => i.user));
      }
    } catch {
      toast.error('Failed to load partner favorites');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleProjectDrillDown = async (projectId: string, projectTitle: string) => {
    openDrillDown(projectTitle);
    try {
      const response = await api.get<{ success: boolean; data: { interests: Array<{ user: DrillDownUser }> } }>(
        `/admin/projects/${projectId}/interests`
      );
      if (response.success && response.data) {
        setDialogUsers(response.data.interests.map(i => i.user));
      }
    } catch {
      toast.error('Failed to load project favorites');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleMetricDrillDown = async (
    segment: 'registered' | 'checked_in' | 'no_shows' | 'walk_ins',
    title: string
  ) => {
    openDrillDown(title);
    const params: Record<string, string> = { limit: '200' };
    if (segment === 'checked_in') params.checkedIn = 'true';
    if (segment === 'no_shows') params.checkedIn = 'false';
    if (segment === 'walk_ins') params.walkIn = 'true';
    if (activeEvent?.id) params.eventId = activeEvent.id;
    try {
      const qs = new URLSearchParams(params).toString();
      const response = await api.get<{ success: boolean; data: DrillDownUser[] }>(`/admin/users?${qs}`);
      if (response.success) setDialogUsers(response.data as DrillDownUser[]);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleParticipantTypeDrillDown = async (participantType: string, label: string) => {
    openDrillDown(label);
    try {
      const drillParams = new URLSearchParams({ participantType, limit: '200' });
      if (activeEvent?.id) drillParams.set('eventId', activeEvent.id);
      const response = await api.get<{ success: boolean; data: DrillDownUser[] }>(
        `/admin/users?${drillParams}`
      );
      if (response.success) setDialogUsers(response.data as DrillDownUser[]);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSessionTypeDrillDown = async (sessionType: string, label: string) => {
    openDrillDown(`${label} Sessions`, 'sessions');
    try {
      const sessionParams = new URLSearchParams({ sessionType });
      if (activeEvent?.id) sessionParams.set('eventId', activeEvent.id);
      const response = await api.get<{ success: boolean; data: Array<{ id: string; title: string; sessionType: string; startTime: string; capacity: number | null; _count: { rsvps: number } }> }>(
        `/admin/sessions?${sessionParams}`
      );
      if (response.success) {
        setDialogSessions(response.data.map(s => ({
          id: s.id,
          title: s.title,
          sessionType: s.sessionType,
          startTime: s.startTime,
          capacity: s.capacity,
          confirmedCount: s._count?.rsvps ?? 0,
        })));
      }
    } catch {
      toast.error('Failed to load sessions');
    } finally {
      setDialogLoading(false);
    }
  };

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  // ── Skeletons ──

  const MetricCardSkeleton = () => (
    <Card className="p-3 md:p-4">
      <div className="flex items-center gap-2 mb-1 md:mb-2">
        <Skeleton className="w-8 h-8 md:w-10 md:h-10 rounded-lg" />
        <Skeleton className="h-3 w-16" />
      </div>
      <Skeleton className="h-8 w-20 mb-1" />
    </Card>
  );

  const ChartSkeleton = () => (
    <Card className="p-3 md:p-4">
      <Skeleton className="h-4 w-32 mb-3" />
      <Skeleton className="w-full h-48 md:h-56 rounded" />
    </Card>
  );

  const ListSkeleton = () => (
    <Card className="p-3 md:p-4">
      <Skeleton className="h-4 w-32 mb-3" />
      <div className="space-y-2.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted">
            <Skeleton className="w-6 h-6 rounded-lg flex-shrink-0" />
            <div className="flex-1"><Skeleton className="h-3 w-24 mb-1" /><Skeleton className="h-2 w-full rounded-full" /></div>
            <Skeleton className="h-5 w-8 rounded" />
          </div>
        ))}
      </div>
    </Card>
  );

  // ── Shell wrapper ──

  const Shell = ({ children }: { children: React.ReactNode }) => {
    if (isDesktop) return <DesktopShell>{children}</DesktopShell>;
    return <div className="min-h-screen bg-background pb-24">{children}</div>;
  };

  // ── Error state ──

  if (!loading && !data) {
    return (
      <Shell>
        <div className="h-full flex items-center justify-center bg-background">
          <p className="text-muted-foreground">Failed to load analytics</p>
        </div>
      </Shell>
    );
  }

  // ── Data defaults ──

  const { realTimeMetrics, demographics, sessionAnalytics, networkingEngagement } = data || {
    realTimeMetrics: { totalRegistered: 0, checkedIn: 0, checkInRate: 0, noShows: 0, walkIns: 0, checkInsLastHour: 0, checkInsByMethod: [] },
    demographics: { byParticipantType: [], byRole: [], byOrganization: [] },
    sessionAnalytics: { sessions: [], rsvpsByStatus: [], typePopularity: [], totalCapacity: 0, totalConfirmed: 0 },
    networkingEngagement: { totalConnections: 0, connectionsLast24h: 0, connectionsByMethod: [], totalMessages: 0, totalConversations: 0, connectionGraph: {}, projectInterest: [], partnerInterest: [] },
  };

  // ── Chart data ──

  const participantTypeChartData = demographics.byParticipantType.map((p) => ({
    name: PARTICIPANT_TYPE_LABELS[p.participantType as keyof typeof PARTICIPANT_TYPE_LABELS] || p.participantType,
    value: p.count,
    fill: COLORS[p.participantType] || '#6b7280',
  }));

  const typeChartData = sessionAnalytics.typePopularity
    .filter((t) => t.sessionType in SESSION_TYPE_LABELS)
    .map((t, i) => ({
      name: SESSION_TYPE_LABELS[t.sessionType as keyof typeof SESSION_TYPE_LABELS],
      sessionType: t.sessionType,
      value: t.count,
      fill: TRACK_COLORS[i % TRACK_COLORS.length],
    }));

  // ── Render ──

  return (
    <Shell>
      <div className={isDesktop ? 'h-full flex flex-col' : ''}>
        {/* Title Bar */}
        {isDesktop ? (
          <div className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Analytics
                </h1>
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-refreshes every 5 min &middot; Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAnalytics()}
                aria-label="Refresh analytics"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                Refresh
              </Button>
            </div>
          </div>
        ) : (
          <div className="container mx-auto px-3 pt-2">
            <header className="bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg sticky top-0 z-10 rounded-lg">
              <div className="px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate('/')}
                      className="text-white hover:bg-card/20 h-11 w-11"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <div>
                      <h1 className="text-base font-bold flex items-center gap-1.5">
                        <BarChart3 className="h-4 w-4" />
                        Analytics
                      </h1>
                      <p className="text-xs text-blue-200">
                        Last updated: {lastUpdated.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => fetchAnalytics()}
                    className="text-white hover:bg-card/20 h-9 w-9"
                    aria-label="Refresh analytics"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </header>
          </div>
        )}

        <div className={isDesktop ? 'flex-1 overflow-y-auto px-6 py-6 space-y-6' : 'container mx-auto px-3 py-4 space-y-4'}>
          {/* ─── DEMOGRAPHICS ─── */}
          <section>
            <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" />
              Demographics
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => <MetricCardSkeleton key={i} />)
              ) : (
                <>
                  <Card className="p-3 md:p-4 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-card border-blue-100 dark:border-blue-900/50 cursor-pointer hover:ring-2 hover:ring-blue-200 dark:hover:ring-blue-800 transition-all" onClick={() => handleMetricDrillDown('registered', 'All Registered Users')}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-blue-100"><Users className="h-3.5 w-3.5 text-blue-600" /></div>
                      <span className="text-[10px] md:text-xs text-muted-foreground font-medium">Registered</span>
                    </div>
                    <p className="text-xl md:text-3xl font-bold text-foreground">{realTimeMetrics.totalRegistered}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">All users with an account</p>
                  </Card>
                  <Card className="p-3 md:p-4 bg-gradient-to-br from-green-50 to-white dark:from-green-950/30 dark:to-card border-green-100 dark:border-green-900/50 cursor-pointer hover:ring-2 hover:ring-green-200 dark:hover:ring-green-800 transition-all" onClick={() => handleMetricDrillDown('checked_in', 'Checked In Users')}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-green-100"><UserCheck className="h-3.5 w-3.5 text-green-600" /></div>
                      <span className="text-[10px] md:text-xs text-muted-foreground font-medium">Checked In</span>
                    </div>
                    <p className="text-xl md:text-3xl font-bold text-foreground">{realTimeMetrics.checkedIn}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">Scanned QR or marked present ({realTimeMetrics.checkInRate}%)</p>
                  </Card>
                  <Card className="p-3 md:p-4 bg-gradient-to-br from-red-50 to-white dark:from-red-950/30 dark:to-card border-red-100 dark:border-red-900/50 cursor-pointer hover:ring-2 hover:ring-red-200 dark:hover:ring-red-800 transition-all" onClick={() => handleMetricDrillDown('no_shows', 'No Shows')}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-red-100"><UserX className="h-3.5 w-3.5 text-red-600" /></div>
                      <span className="text-[10px] md:text-xs text-muted-foreground font-medium">No Shows</span>
                    </div>
                    <p className="text-xl md:text-3xl font-bold text-foreground">{realTimeMetrics.noShows}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">Registered but not checked in</p>
                  </Card>
                  <Card className="p-3 md:p-4 bg-gradient-to-br from-purple-50 to-white dark:from-purple-950/30 dark:to-card border-purple-100 dark:border-purple-900/50 cursor-pointer hover:ring-2 hover:ring-purple-200 dark:hover:ring-purple-800 transition-all" onClick={() => handleMetricDrillDown('walk_ins', 'Walk-in Users')}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 rounded-lg bg-purple-100"><UserPlus className="h-3.5 w-3.5 text-purple-600" /></div>
                      <span className="text-[10px] md:text-xs text-muted-foreground font-medium">Walk-ins</span>
                    </div>
                    <p className="text-xl md:text-3xl font-bold text-foreground">{realTimeMetrics.walkIns}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">On-site registrations without prior signup</p>
                  </Card>
                </>
              )}
            </div>
          </section>

          {/* ─── NETWORK ENGAGEMENT ─── */}
          <section>
            <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Network className="h-4 w-4 text-purple-500" />
              Network Engagement
            </h2>

            <div className="grid grid-cols-3 gap-3">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Card key={i} className="p-3 md:p-4 text-center">
                    <Skeleton className="h-6 w-6 mx-auto mb-1 rounded" />
                    <Skeleton className="h-6 w-12 mx-auto mb-1" />
                    <Skeleton className="h-3 w-20 mx-auto" />
                  </Card>
                ))
              ) : (
                <>
                  <Card className="p-3 md:p-4 text-center">
                    <Link2 className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                    <p className="text-lg md:text-2xl font-bold text-foreground">{networkingEngagement.totalConnections}</p>
                    <p className="text-[10px] md:text-xs text-muted-foreground font-medium">Total Connections</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">Users who favorited another user</p>
                  </Card>
                  <Card className="p-3 md:p-4 text-center">
                    <MessageSquare className="h-5 w-5 mx-auto mb-1 text-purple-500" />
                    <p className="text-lg md:text-2xl font-bold text-foreground">{networkingEngagement.totalMessages}</p>
                    <p className="text-[10px] md:text-xs text-muted-foreground font-medium">Messages</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">Total messages sent across all chats</p>
                  </Card>
                  <Card className="p-3 md:p-4 text-center">
                    <Users className="h-5 w-5 mx-auto mb-1 text-amber-500" />
                    <p className="text-lg md:text-2xl font-bold text-foreground">{networkingEngagement.totalConversations}</p>
                    <p className="text-[10px] md:text-xs text-muted-foreground font-medium">Conversations</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">Unique chat threads between participants</p>
                  </Card>
                </>
              )}
            </div>
          </section>

          {/* ─── Top Organizations ─── */}
          {loading ? (
            <ListSkeleton />
          ) : (
            <Card className="p-3 md:p-4">
              <h3 className="text-xs md:text-sm font-semibold text-foreground mb-3">
                <Building2 className="inline h-3.5 w-3.5 mr-1" />
                Top Organizations
                {demographics.byOrganization.length > 0 && (
                  <span className="text-[10px] font-normal text-muted-foreground ml-2">Tap to view members</span>
                )}
              </h3>
              <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                {demographics.byOrganization.length > 0 ? (
                  demographics.byOrganization.map((org, i) => {
                    const maxCount = demographics.byOrganization[0]?.count || 1;
                    const percentage = (org.count / maxCount) * 100;
                    const barColor = ORG_COLORS[i % ORG_COLORS.length];
                    return (
                      <div
                        key={org.organization}
                        className="group cursor-pointer"
                        onClick={() => handleOrgDrillDown(org.organization)}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: barColor }}>
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium text-foreground truncate pr-2">{org.organization}</span>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Badge variant="secondary" className="text-[10px]" style={{ backgroundColor: `${barColor}15`, color: barColor }}>{org.count}</Badge>
                                <ChevronRight className="h-3 w-3 text-muted-foreground" />
                              </div>
                            </div>
                            <div className="w-full bg-muted/50 rounded-full h-2 overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-500 group-hover:opacity-80" style={{ width: `${percentage}%`, backgroundColor: barColor }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">No organization data</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* ─── SESSION ANALYTICS: Popularity | Fill Rates (side by side) ─── */}
          <section>
            <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-green-500" />
              Session Analytics
            </h2>

            {loading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <ChartSkeleton />
                <ListSkeleton />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Popularity by Session Type */}
                <Card className="p-3 md:p-4">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground mb-3">
                    Popularity by Session Type
                    {typeChartData.length > 0 && (
                      <span className="text-[10px] font-normal text-muted-foreground ml-2">Tap to view sessions</span>
                    )}
                  </h3>
                  {typeChartData.length > 0 ? (
                    <div className="h-48 md:h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={typeChartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar
                            dataKey="value"
                            name="RSVPs"
                            radius={[0, 4, 4, 0]}
                            cursor="pointer"
                            onClick={(data) => {
                              const d = data as unknown as { sessionType?: string; name?: string };
                              handleSessionTypeDrillDown(d.sessionType ?? '', d.name ?? '');
                            }}
                          >
                            {typeChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-48 md:h-56 flex items-center justify-center text-muted-foreground">
                      <p className="text-xs">No session data available</p>
                    </div>
                  )}
                </Card>

                {/* Session Fill Rates */}
                <Card className="p-3 md:p-4">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground mb-3">
                    Session Fill Rates
                    {sessionAnalytics.sessions.length > 0 && (
                      <span className="text-[10px] font-normal text-muted-foreground ml-2">Tap to view registrants</span>
                    )}
                  </h3>
                  {sessionAnalytics.sessions.length > 0 ? (
                    <>
                      <div className="space-y-2 max-h-52 overflow-y-auto">
                        {sessionAnalytics.sessions.map((session) => (
                          <div
                            key={session.id}
                            className="cursor-pointer p-1.5 -mx-1.5 rounded-lg transition-colors hover:bg-muted/50"
                            onClick={() => handleSessionDrillDown(session.id, session.title)}
                          >
                            <div className="flex justify-between items-center mb-0.5">
                              <span className="text-xs font-medium text-foreground truncate max-w-[200px]">{session.title}</span>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-muted-foreground">{session.confirmed}/{session.capacity || '?'}</span>
                                <ChevronRight className="h-3 w-3 text-muted-foreground" />
                              </div>
                            </div>
                            <Progress
                              value={session.fillRate}
                              className={`h-2 ${session.fillRate >= 90 ? '[&>div]:bg-red-500' : session.fillRate >= 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-green-500'}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Total Capacity</span>
                          <span className="font-medium">{sessionAnalytics.totalCapacity}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Total Confirmed</span>
                          <span className="font-medium text-green-600">{sessionAnalytics.totalConfirmed}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="h-48 md:h-56 flex items-center justify-center text-muted-foreground">
                      <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">No sessions scheduled</p>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </section>

          {/* ─── Participant Types | Connection Patterns (side by side) ─── */}
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <ChartSkeleton />
              <ChartSkeleton />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Participant Types */}
              <Card className="p-3 md:p-4">
                <h3 className="text-xs md:text-sm font-semibold text-foreground mb-3">
                  Participant Types
                  {participantTypeChartData.length > 0 && (
                    <span className="text-[10px] font-normal text-muted-foreground ml-2">Tap to view participants</span>
                  )}
                </h3>
                {participantTypeChartData.length > 0 ? (
                  <div className="h-48 md:h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={participantTypeChartData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }} barSize={20}>
                        <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={55} />
                        <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
                        <Bar
                          dataKey="value"
                          name="Count"
                          radius={[0, 4, 4, 0]}
                          cursor="pointer"
                          onClick={(_data, index: number) => {
                            const entry = participantTypeChartData[index];
                            const pt = demographics.byParticipantType[index]?.participantType ?? '';
                            if (entry) handleParticipantTypeDrillDown(pt, entry.name);
                          }}
                        >
                          {participantTypeChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-48 md:h-56 flex flex-col items-center justify-center text-muted-foreground">
                    <Users className="h-8 w-8 mb-2 opacity-50" />
                    <p className="text-xs">No participant data yet</p>
                  </div>
                )}
              </Card>

              {/* Connection Patterns */}
              <Card className="p-3 md:p-4">
                <h3 className="text-xs md:text-sm font-semibold text-foreground mb-3">Connection Patterns</h3>
                {Object.keys(networkingEngagement.connectionGraph).length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr>
                            <th className="p-1.5 text-left text-muted-foreground">From / To</th>
                            {Object.keys(networkingEngagement.connectionGraph).map((role) => (
                              <th key={role} className="p-1.5 text-center" style={{ color: COLORS[role] }}>
                                {(PARTICIPANT_TYPE_LABELS[role as keyof typeof PARTICIPANT_TYPE_LABELS] || role).split(' ').map((w) => w[0]).join('')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(networkingEngagement.connectionGraph).map(([fromRole, toRoles]) => (
                            <tr key={fromRole}>
                              <td
                                className="p-1.5 font-medium cursor-pointer hover:underline"
                                style={{ color: COLORS[fromRole] }}
                                onClick={() => handleParticipantTypeDrillDown(
                                  fromRole,
                                  PARTICIPANT_TYPE_LABELS[fromRole as keyof typeof PARTICIPANT_TYPE_LABELS] || fromRole
                                )}
                              >
                                {PARTICIPANT_TYPE_LABELS[fromRole as keyof typeof PARTICIPANT_TYPE_LABELS] || fromRole}
                              </td>
                              {Object.entries(toRoles).map(([toRole, count]) => {
                                const maxVal = Math.max(...Object.values(networkingEngagement.connectionGraph).flatMap((r) => Object.values(r)));
                                const intensity = maxVal > 0 ? (count / maxVal) * 100 : 0;
                                return (
                                  <td key={toRole} className="p-1.5 text-center rounded" style={{
                                    backgroundColor: `rgba(59, 130, 246, ${intensity / 100})`,
                                    color: intensity > 50 ? 'white' : 'inherit',
                                  }}>
                                    {count}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Click a row label to view participants</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {Object.entries(PARTICIPANT_TYPE_LABELS).filter(([key]) => !['faculty', 'guest'].includes(key)).map(([, v]) => `${v.split(' ').map((w) => w[0]).join('')}=${v}`).join(', ')}
                    </p>
                  </>
                ) : (
                  <div className="h-48 md:h-56 flex items-center justify-center text-muted-foreground">
                    <Network className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">No connection data yet</p>
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ─── COLLABORATION OPPORTUNITIES ─── */}
          <section>
            <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Heart className="h-4 w-4 text-pink-500" />
              Collaboration Opportunities
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {loading ? (
                <>
                  <ListSkeleton />
                  <ListSkeleton />
                </>
              ) : (
                <>
                  {/* Industry Favorites */}
                  <Card className="p-3 md:p-4">
                    <h3 className="text-xs md:text-sm font-semibold text-foreground mb-3">
                      Industry Favorites
                      {networkingEngagement.partnerInterest.length > 0 && (
                        <span className="text-[10px] font-normal text-muted-foreground ml-2">Tap to view participants</span>
                      )}
                    </h3>
                    {networkingEngagement.partnerInterest.length > 0 ? (
                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {networkingEngagement.partnerInterest.map((partner, i) => (
                          <div
                            key={partner.id}
                            className="flex items-center justify-between p-2 rounded-lg bg-muted cursor-pointer hover:bg-muted/80"
                            onClick={() => handlePartnerDrillDown(partner.id, partner.name)}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-xs font-bold text-muted-foreground w-4">{i + 1}</span>
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-foreground truncate">{partner.name}</p>
                                {partner.organizationType && (
                                  <Badge variant="outline" className="text-[10px] mt-0.5">{partner.organizationType}</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <Badge className="bg-pink-100 text-pink-700 text-xs">{partner.interested}</Badge>
                              <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-xs">No partner data yet</p>
                      </div>
                    )}
                  </Card>

                  {/* Project Favorites */}
                  <Card className="p-3 md:p-4">
                    <h3 className="text-xs md:text-sm font-semibold text-foreground mb-3">
                      Project Favorites
                      {networkingEngagement.projectInterest.length > 0 && (
                        <span className="text-[10px] font-normal text-muted-foreground ml-2">Tap to view participants</span>
                      )}
                    </h3>
                    {networkingEngagement.projectInterest.length > 0 ? (
                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {networkingEngagement.projectInterest.map((project, i) => (
                          <div
                            key={project.id}
                            className="flex items-center justify-between p-2 rounded-lg bg-muted cursor-pointer hover:bg-muted/80"
                            onClick={() => handleProjectDrillDown(project.id, project.title)}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-xs font-bold text-muted-foreground w-4">{i + 1}</span>
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-foreground truncate">{project.title}</p>
                                <Badge variant="outline" className="text-[10px] mt-0.5">{project.stage}</Badge>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <Badge className="bg-blue-100 text-blue-700 text-xs">{project.interested}</Badge>
                              <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-xs">No project data yet</p>
                      </div>
                    )}
                  </Card>
                </>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* ─── Unified Drill-Down Dialog ─── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[80vh] overflow-hidden rounded-lg border border-border mx-auto p-0" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-4 py-3 rounded-t-lg">
            <DialogTitle className="text-white text-sm font-semibold">{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-4 py-3 max-h-[calc(80vh-3.5rem)]">

          {dialogLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : dialogMode === 'sessions' ? (
            dialogSessions.length > 0 ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {dialogSessions.length} session{dialogSessions.length !== 1 ? 's' : ''} — tap to view registrants
                </p>
                <div className="space-y-2">
                  {dialogSessions.map((s) => (
                    <div
                      key={s.id}
                      className="p-3 rounded-lg bg-muted cursor-pointer hover:bg-muted/80 transition-colors"
                      onClick={() => handleSessionDrillDown(s.id, s.title)}
                    >
                      <p className="text-sm font-medium">{s.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-[10px]">
                          {SESSION_TYPE_LABELS[s.sessionType as keyof typeof SESSION_TYPE_LABELS] || s.sessionType}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {s.confirmedCount}/{s.capacity ?? '?'} confirmed
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground">No sessions found</p>
              </div>
            )
          ) : dialogUsers.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">
                {dialogUsers.length} participant{dialogUsers.length !== 1 ? 's' : ''}
              </p>
              <div className="space-y-2">
                {dialogUsers.map((u) => (
                  <div key={u.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted">
                    <Avatar className="h-9 w-9 flex-shrink-0">
                      <AvatarImage src={u.avatarUrl || undefined} />
                      <AvatarFallback
                        className="text-white text-xs"
                        style={{ backgroundColor: COLORS[u.role || ''] || '#6b7280' }}
                      >
                        {getInitials(displayName(u))}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{displayName(u)}</span>
                        {u.participantType && <Badge variant="outline" className="text-[10px]">{PARTICIPANT_TYPE_LABELS[u.participantType as keyof typeof PARTICIPANT_TYPE_LABELS] || u.participantType}</Badge>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
                        {u.organization && (
                          <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{u.organization}</span>
                        )}
                        {u.department && <span className="text-muted-foreground/70">{u.department}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <a href={`mailto:${u.email}`} className="flex items-center gap-1 text-xs text-primary hover:underline">
                          <Mail className="h-3 w-3" /> Email
                        </a>
                        {u.linkedinUrl && (
                          <a href={u.linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                            <Linkedin className="h-3 w-3" /> LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No data available</p>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
